import { api, type Source, type SourceKind } from './api';

// ---------------------------------------------------------------------------
// Capturing the cameras that are plugged in.
//
// The Record screen is one camera and a prompter: a presenter reading a line.
// An interviewer with two cameras and a lapel mic, a workshop with a wide and
// a close on the bench, a listing agent with a camera on a tripod and one in
// hand, want something plainer — every connected camera on screen at once,
// each one recordable, all of them rolling together on one press. Each camera
// becomes its own take in Sources, and from there a scene on the timeline.
//
// The arithmetic that decides what a take is called and which camera carries
// the sound lives here, away from the component, where the tests can read it.
// ---------------------------------------------------------------------------

/** A camera the browser can see. The shape enumerateDevices gives, narrowed. */
export interface CameraInfo {
  deviceId: string;
  label: string;
}

/** What a camera is called on the rig: its own label, or a number when the browser withholds it. */
export function cameraName(d: Pick<CameraInfo, 'label'>, index: number): string {
  const label = d.label.trim();
  return label || `Camera ${index + 1}`;
}

/**
 * A take's name.
 *
 *   label, one camera     → "Cold open — take 2"
 *   label, several        → "Cold open — take 2 · Logitech C920"
 *   no label              → "Logitech C920 — take 2"
 *
 * The camera's name goes on when more than one is rolling, because two takes
 * both called "Cold open — take 2" are indistinguishable on the timeline, and
 * the whole point of two cameras is to tell the angles apart.
 */
export function takeNameFor(input: { label: string; camera: string; n: number; several: boolean }): string {
  const label = input.label.trim();
  const base = label ? `${label} — take ${input.n}` : `${input.camera} — take ${input.n}`;
  return label && input.several ? `${base} · ${input.camera}` : base;
}

/**
 * Which rolling camera the microphone rides with.
 *
 * One microphone, one set of words: the sound goes on ONE take so the scene
 * that cuts to it speaks, and the other angles are picture only — the same
 * rule the Record screen's second camera follows. The creator's choice wins
 * when it is among the cameras rolling; otherwise the first one rolling.
 */
export function soundCarrier(rolling: readonly string[], chosen: string | null): string | null {
  if (!rolling.length) return null;
  if (chosen && rolling.includes(chosen)) return chosen;
  return rolling[0] ?? null;
}

/** The stream a recorder takes for one camera: its picture, and the microphone when this camera carries the sound. */
export function recorderStream(camera: MediaStream, mic: MediaStream | null, carriesSound: boolean): MediaStream {
  const tracks = [...camera.getVideoTracks()];
  if (carriesSound && mic) tracks.push(...mic.getAudioTracks());
  return new MediaStream(tracks);
}

/**
 * A recorded take, into Sources: the same three steps every take makes —
 * a source row with a signed upload, the bytes straight to storage, ingest.
 * Shared by the camera rig; the Record screen does the same by hand.
 */
export async function uploadTake(input: {
  blob: Blob; ext: string; name: string; kind: Extract<SourceKind, 'camera' | 'screen'>;
  brandId: string | null; said?: string;
}): Promise<Source> {
  const contentType = input.blob.type || `video/${input.ext}`;
  const created = await api.createSource({
    name: input.name, kind: input.kind, brandId: input.brandId, mode: 'stored',
    filename: `take-${Date.now()}.${input.ext}`, contentType,
  });
  if (!created.upload) throw new Error('no upload was offered for the take');
  const put = await fetch(created.upload.url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: input.blob });
  if (!put.ok) throw new Error(`upload failed (${put.status})`);
  await api.ingestSource(created.source.id);
  if (input.said?.trim()) await api.updateSource(created.source.id, { said: input.said.trim() }).catch(() => undefined);
  return created.source;
}

/** Why a camera did not open, in words a creator can act on. */
export function cameraError(e: unknown): string {
  const name = (e as DOMException)?.name;
  if (name === 'NotAllowedError') return 'the browser did not allow the camera. Allow it for this site and try again.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'no camera was found. Plug one in, then open the cameras again.';
  if (name === 'NotReadableError' || name === 'AbortError') return 'the camera is in use by another app. Close it there and try again.';
  return `the camera did not open: ${(e as Error)?.message ?? String(e)}`;
}
